import React, { useState, useEffect } from 'react';
import { FaExclamationTriangle, FaTimes, FaClock, FaHourglassHalf } from 'react-icons/fa';
import { getMaintenanceBanner } from '../api/client';

const MaintenanceBanner = () => {
  const [banner, setBanner] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const wasDismissed = sessionStorage.getItem('maintenance_banner_dismissed');
    if (wasDismissed) {
      setDismissed(true);
      return;
    }

    const fetchBanner = async () => {
      try {
        const data = await getMaintenanceBanner();
        if (data?.active) {
          setBanner(data);
        }
      } catch (err) {
        // Silently fail — banner is non-critical
      }
    };
    fetchBanner();
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('maintenance_banner_dismissed', 'true');
  };

  if (!banner || dismissed) return null;

  const scheduledDate = banner.scheduled_at
    ? new Date(banner.scheduled_at).toLocaleString(undefined, {
        weekday: 'short', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : null;

  return (
    <div className="maintenance-banner relative overflow-hidden rounded-xl mb-5 shadow-lg border border-amber-300">
      {/* Animated gradient background */}
      <div className="maintenance-banner-bg absolute inset-0" />

      <div className="relative flex items-start gap-3 px-5 py-4">
        {/* Flashing icon */}
        <div className="maintenance-flash-icon flex-shrink-0 mt-0.5">
          <div className="w-9 h-9 bg-white/90 rounded-lg flex items-center justify-center shadow-sm">
            <FaExclamationTriangle className="text-amber-600 text-lg" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-amber-900 uppercase tracking-wide mb-1">
            System Maintenance
          </p>
          <p className="text-sm text-amber-800 font-medium leading-relaxed">
            {banner.message}
          </p>
          {(scheduledDate || banner.downtime_hours) && (
            <div className="flex flex-wrap items-center gap-3 mt-2">
              {scheduledDate && (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-white/70 px-2.5 py-1 rounded-full">
                  <FaClock className="text-amber-500" />
                  {scheduledDate}
                </span>
              )}
              {banner.downtime_hours && (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-white/70 px-2.5 py-1 rounded-full">
                  <FaHourglassHalf className="text-amber-500" />
                  ~{banner.downtime_hours}h downtime
                </span>
              )}
            </div>
          )}
        </div>

        {/* Dismiss */}
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 p-1.5 rounded-lg hover:bg-white/40 transition-colors"
          aria-label="Dismiss"
        >
          <FaTimes className="text-amber-700 text-sm" />
        </button>
      </div>

      <style>{`
        .maintenance-banner-bg {
          background: linear-gradient(135deg, #fef3c7, #fdba74, #fef3c7, #fde68a, #fed7aa);
          background-size: 300% 300%;
          animation: maintenanceShimmer 3s ease-in-out infinite;
        }
        @keyframes maintenanceShimmer {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .maintenance-flash-icon {
          animation: flashPulse 2s ease-in-out infinite;
        }
        @keyframes flashPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(0.92); }
        }
      `}</style>
    </div>
  );
};

export default MaintenanceBanner;
